import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import toast from 'react-hot-toast';
import { FaMapMarkerAlt, FaCalendarAlt, FaClock, FaSearch, FaRoute } from 'react-icons/fa';
import MapContainer from '../components/MapContainer';
import { geocodeAddress, getCurrentLocation, reverseGeocode } from '../utils/maps';
import AutocompleteInput from '../components/AutocompleteInput';
import { FaCrosshairs } from 'react-icons/fa';
import { cleanAddress } from '../utils/addressUtils';

const SearchTrips = () => {
  const location = useLocation();
  const [searchData, setSearchData] = useState({
    origin: location.state?.origin || '',
    destination: location.state?.destination || '',
    date: '',
    time: '',
    vehicleType: location.state?.vehicleType || 'all'
  });
  const [originCoords, setOriginCoords] = useState(null);
  const [destCoords, setDestCoords] = useState(null);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState({ origin: false, destination: false });
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  // Auto-search when pre-filled data is provided from Home page
  useEffect(() => {
    if (location.state?.origin && location.state?.destination) {
      // Auto-trigger search when navigated from Home page with pre-filled data
      const performSearch = async () => {
        setLoading(true);
        try {
          // Geocode addresses to get coordinates
          const [originGeo, destGeo] = await Promise.all([
            geocodeAddress(location.state.origin),
            geocodeAddress(location.state.destination)
          ]);

          const response = await api.get('/api/trips/search', {
            params: {
              origin: `${originGeo.lat},${originGeo.lng}`,
              destination: `${destGeo.lat},${destGeo.lng}`,
              date: location.state?.date || searchData.date
            }
          });
          
          let filteredTrips = response.data?.trips || response.data || [];
          if (!Array.isArray(filteredTrips)) filteredTrips = [];
          const vehicleType = location.state?.vehicleType || searchData.vehicleType || 'all';
          if (vehicleType !== 'all') {
            filteredTrips = filteredTrips.filter(trip => 
              trip.rider?.vehicleInfo?.type === vehicleType
            );
          }
          
          setTrips(Array.isArray(filteredTrips) ? filteredTrips : []);
          if (filteredTrips.length > 0) {
            toast.success(`Found ${filteredTrips.length} trips`);
          } else {
            toast('No trips found for your search criteria', { icon: 'ℹ️' });
          }
        } catch (error) {
          console.error('Search error:', error);
          toast.error(error.message || 'Failed to search trips');
        } finally {
          setLoading(false);
        }
      };
      
      performSearch();
    }
    // Clear location state after processing to prevent re-searching on re-renders
    if (location.state) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleChange = (e) => {
    setSearchData({
      ...searchData,
      [e.target.name]: e.target.value
    });
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Trim whitespace and check if fields are actually filled
    const origin = searchData.origin?.trim();
    const destination = searchData.destination?.trim();
    
    if (!origin || origin.length === 0) {
      toast.error('Please enter an origin location');
      return;
    }
    
    if (!destination || destination.length === 0) {
      toast.error('Please enter a destination location');
      return;
    }

    setLoading(true);
    let hasErrors = false;
    
    try {
      // Use coordinates if available from autocomplete, otherwise geocode
      let originGeo, destGeo;
      
      // Handle origin
      if (originCoords) {
        originGeo = {
          lat: originCoords.lat,
          lng: originCoords.lng,
          formatted_address: origin
        };
      } else {
        try {
          originGeo = await geocodeAddress(origin);
        } catch (geoError) {
          hasErrors = true;
          toast.error(
            `Origin address "${origin}" not found.\n\n💡 Please select an address from the suggestions dropdown when typing.`,
            { duration: 6000 }
          );
          setLoading(false);
          return;
        }
      }

      // Handle destination
      if (destCoords) {
        destGeo = {
          lat: destCoords.lat,
          lng: destCoords.lng,
          formatted_address: destination
        };
      } else {
        try {
          destGeo = await geocodeAddress(destination);
        } catch (geoError) {
          hasErrors = true;
          toast.error(
            `Destination address "${destination}" not found.\n\n💡 Please select an address from the suggestions dropdown when typing.`,
            { duration: 6000 }
          );
          setLoading(false);
          return;
        }
      }

      // If we got here, both addresses were geocoded successfully
      const response = await api.get('/api/trips/search', {
        params: {
          origin: `${originGeo.lat},${originGeo.lng}`,
          destination: `${destGeo.lat},${destGeo.lng}`,
          ...(searchData.date && searchData.date.trim() !== '' && { date: searchData.date })
        }
      });
      
      // Filter by vehicle type on frontend
      let filteredTrips = response.data?.trips || response.data || [];
      if (!Array.isArray(filteredTrips)) filteredTrips = [];
      if (searchData.vehicleType !== 'all') {
        filteredTrips = filteredTrips.filter(trip => 
          trip.rider?.vehicleInfo?.type === searchData.vehicleType
        );
      }
      
      setTrips(Array.isArray(filteredTrips) ? filteredTrips : []);
      toast.success(`Found ${filteredTrips.length} trips`);
    } catch (error) {
      console.error('Search error:', error);
      // Only show error if we haven't already shown a geocoding error
      if (!hasErrors) {
        const errorMsg = error.message || 'Failed to search trips. Please check your addresses.';
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOriginSelect = (place) => {
    setOriginCoords(place.coordinates);
  };

  const handleDestinationSelect = (place) => {
    setDestCoords(place.coordinates);
  };

  const handleBookTrip = (tripId) => {
    if (!isAuthenticated) {
      toast.error('Please login to book a trip');
      // Preserve current location so user can return after login
      navigate('/login', { state: { from: { pathname: '/search' }, tripId } });
      return;
    }
    navigate(`/trip/${tripId}`);
  };

  const handleShowTripOnMap = (trip) => {
    setSelectedTrip(trip);
    setShowMap(true);
  };

  const handleUseCurrentLocation = async (field) => {
    setLoadingLocation(prev => ({ ...prev, [field]: true }));
    try {
      // Get current location
      const location = await getCurrentLocation();
      
      // Reverse geocode to get address
      const addressData = await reverseGeocode(location.lat, location.lng);
      
      // Update form data
      if (field === 'origin') {
        setSearchData(prev => ({ ...prev, origin: addressData.address }));
        setOriginCoords({ lat: addressData.lat, lng: addressData.lng });
        toast.success('Current location set as origin');
      } else {
        setSearchData(prev => ({ ...prev, destination: addressData.address }));
        setDestCoords({ lat: addressData.lat, lng: addressData.lng });
        toast.success('Current location set as destination');
      }
    } catch (error) {
      console.error('Error getting current location:', error);
      if (error.message.includes('denied')) {
        toast.error('Location access denied. Please enable location permissions in your browser settings.');
      } else if (error.message.includes('timeout')) {
        toast.error('Location request timed out. Please try again.');
      } else {
        toast.error('Failed to get current location. Please try again.');
      }
    } finally {
      setLoadingLocation(prev => ({ ...prev, [field]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 sm:mb-8 text-center px-2">
            Find Your Perfect Ride
          </h1>

          {/* Search Form */}
          <div className="card mb-6 sm:mb-8 relative z-0">
            <form onSubmit={handleSearch} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 relative z-0">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaMapMarkerAlt className="inline mr-2" />
                    From (Your Location)
                  </label>
                  <div className="relative">
                    <AutocompleteInput
                      name="origin"
                      value={searchData.origin}
                      onChange={handleChange}
                      onPlaceSelect={(place) => {
                        setSearchData(prev => ({ ...prev, origin: place.address }));
                        setOriginCoords(place.coordinates);
                      }}
                      placeholder="Start typing or use current location..."
                      required
                      className="input-field pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => handleUseCurrentLocation('origin')}
                      disabled={loadingLocation.origin}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center"
                      title="Use My Current Location"
                    >
                      {loadingLocation.origin ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      ) : (
                        <FaCrosshairs className="text-lg" />
                      )}
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-gray-500">
                      {originCoords ? '✓ Address selected' : '💡 Click the crosshair icon to use your current location'}
                    </p>
                    {!originCoords && (
                      <button
                        type="button"
                        onClick={() => handleUseCurrentLocation('origin')}
                        disabled={loadingLocation.origin}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                      >
                        Use My Location
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaMapMarkerAlt className="inline mr-2" />
                    To
                    {destCoords && <span className="ml-2 text-green-600">✓</span>}
                  </label>
                  <div className="relative">
                    <AutocompleteInput
                      name="destination"
                      value={searchData.destination}
                      onChange={handleChange}
                      onPlaceSelect={(place) => {
                        setSearchData(prev => ({ ...prev, destination: place.address }));
                        setDestCoords(place.coordinates);
                      }}
                      placeholder="Start typing or use current location..."
                      required
                      className="input-field pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => handleUseCurrentLocation('destination')}
                      disabled={loadingLocation.destination}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center"
                      title="Use My Current Location"
                    >
                      {loadingLocation.destination ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      ) : (
                        <FaCrosshairs className="text-lg" />
                      )}
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-gray-500">
                      {destCoords ? '✓ Address selected' : '💡 Click the crosshair icon to use your current location'}
                    </p>
                    {!destCoords && (
                      <button
                        type="button"
                        onClick={() => handleUseCurrentLocation('destination')}
                        disabled={loadingLocation.destination}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                      >
                        Use My Location
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaCalendarAlt className="inline mr-2" />
                    Date
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={searchData.date}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaClock className="inline mr-2" />
                    Time
                  </label>
                  <input
                    type="time"
                    name="time"
                    value={searchData.time}
                    onChange={handleChange}
                    className="input-field"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vehicle Type
                  </label>
                  <select
                    name="vehicleType"
                    value={searchData.vehicleType}
                    onChange={handleChange}
                    className="input-field"
                  >
                    <option value="all">All Vehicles</option>
                    <optgroup label="Two Wheelers">
                      <option value="bike">Bike</option>
                      <option value="scooter">Scooter</option>
                    </optgroup>
                    <optgroup label="Four Wheelers">
                      <option value="car">Car</option>
                      <option value="sedan">Sedan</option>
                      <option value="hatchback">Hatchback</option>
                      <option value="suv">SUV</option>
                    </optgroup>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center"
              >
                <FaSearch className="mr-2" />
                {loading ? 'Searching...' : 'Search Rides'}
              </button>
            </form>
          </div>

          {/* Map View Toggle */}
          {trips && Array.isArray(trips) && trips.length > 0 && (
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-gray-800">
                Available Rides ({trips.length})
              </h2>
              <button
                onClick={() => setShowMap(!showMap)}
                className="btn-secondary flex items-center"
              >
                <FaRoute className="mr-2" />
                {showMap ? 'Hide Map' : 'Show Map'}
              </button>
            </div>
          )}

          {/* Map View */}
          {showMap && selectedTrip && (
            <div className="card mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Route: {cleanAddress(selectedTrip.origin.address)} → {cleanAddress(selectedTrip.destination.address)}
              </h3>
              <MapContainer
                origin={selectedTrip.origin.coordinates}
                destination={selectedTrip.destination.coordinates}
                waypoints={selectedTrip.waypoints || []}
                height="400px"
                showRoute={true}
              />
              {selectedTrip.waypoints && selectedTrip.waypoints.length > 0 && (
                <div className="mt-4 text-sm text-gray-600">
                  <p className="font-medium mb-2">Stops along the route:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    {selectedTrip.waypoints
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((wp, idx) => (
                        <li key={idx}>{wp.address}</li>
                      ))}
                  </ol>
                </div>
              )}
            </div>
          )}

          {/* Search Results */}
          {trips && Array.isArray(trips) && trips.length > 0 && (
            <div className="space-y-4">
                    {trips.map((trip) => (
                      <div key={trip._id} className="card hover:shadow-lg transition-shadow">
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                          <div className="flex-1 w-full">
                      <div className="flex items-center mb-2">
                        <img
                          src={trip.rider?.photo || '/default-avatar.png'}
                          alt={trip.rider?.name || 'Rider'}
                          className="w-10 h-10 rounded-full mr-3"
                        />
                        <div>
                          <h3 className="font-semibold text-gray-800">
                            {trip.rider?.name || 'Anonymous Rider'}
                          </h3>
                          <div className="flex items-center text-sm text-gray-600">
                            <span className="mr-2">
                              ⭐ {(trip.rider?.rating || 0).toFixed(1)}
                            </span>
                            <span className="mr-2">•</span>
                            <span>{(trip.rider?.totalRides || 0)} rides</span>
                            {trip.rider?.vehicleInfo?.type && (
                              <>
                                <span className="mr-2">•</span>
                                <span className="capitalize">{trip.rider.vehicleInfo.type}</span>
                                {trip.rider.vehicleInfo.seats && (
                                  <span className="ml-1">({trip.rider.vehicleInfo.seats} seats)</span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <div className="flex items-center text-gray-600 mb-1">
                          <FaMapMarkerAlt className="text-green-500 mr-2" />
                          <span>{cleanAddress(trip.origin.address)}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <FaMapMarkerAlt className="text-red-500 mr-2" />
                          <span>{cleanAddress(trip.destination.address)}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <FaClock className="mr-2" />
                        <span>{new Date(trip.departureTime).toLocaleString()}</span>
                        <span className="mx-2">•</span>
                        <span>{trip.availableSeats - trip.bookedSeats}/{trip.availableSeats} seats available</span>
                      </div>
                      
                      {trip.description && (
                        <p className="text-gray-600 text-sm mb-2">{trip.description}</p>
                      )}
                    </div>
                    
                    <div className="text-left sm:text-right w-full sm:w-auto sm:ml-4">
                      <div className="text-xl sm:text-2xl font-bold text-blue-600 mb-2">
                        ₹{user?.role === 'passenger' || !isAuthenticated ? 
                          (trip.price + Math.round(trip.price * 0.1 * 100) / 100) : 
                          trip.price}
                      </div>
                      <div className="space-y-2">
                        <button
                          onClick={() => handleBookTrip(trip._id)}
                          className="btn-primary w-full sm:w-auto"
                        >
                          Book Now
                        </button>
                        <button
                          onClick={() => handleShowTripOnMap(trip)}
                          className="btn-secondary w-full sm:w-auto flex items-center justify-center"
                        >
                          <FaRoute className="mr-1" />
                          View Route
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {trips.length === 0 && !loading && (
            <div className="text-center py-12">
              <FaSearch className="mx-auto text-gray-400 text-6xl mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                No rides found
              </h3>
              <p className="text-gray-500">
                Try adjusting your search criteria or check back later for new rides.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchTrips;